import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowLeft, Image, Settings, FileText, Phone, Mail, MapPin, MessageCircle, Facebook, Instagram, Twitter, Linkedin, Youtube, Menu, ChevronUp, ChevronDown, Edit, Globe, Search, Home, Info, Briefcase, Contact } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CMSDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("general");
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPage, setEditingPage] = useState(null);

  const [siteSettings, setSiteSettings] = useState({
    logo_url: "",
    logo_text: "Roman Cars",
    tagline: "Premium Taxi Service",
    footer_text: "© 2024 Roman Cars. All rights reserved.",
    contact: { phone: "", email: "", address: "", whatsapp: "" },
    social: { facebook: "", instagram: "", twitter: "", linkedin: "", youtube: "" },
    menu_items: [],
    footer_menu: [],
    page_seo: {
      home: { title: "", description: "" },
      about: { title: "", description: "" },
      services: { title: "", description: "" },
      contact: { title: "", description: "" },
      book: { title: "", description: "" }
    }
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pagesRes, settingsRes] = await Promise.all([
        axios.get(`${API}/cms/pages`),
        axios.get(`${API}/cms/site-settings`)
      ]);
      setPages(pagesRes.data.pages || []);
      const s = settingsRes.data.settings || {};
      setSiteSettings({
        logo_url: s.logo_url || "",
        logo_text: s.logo_text || "Roman Cars",
        tagline: s.tagline || "Premium Taxi Service",
        footer_text: s.footer_text || "© 2024 Roman Cars. All rights reserved.",
        contact: s.contact || { phone: "", email: "", address: "", whatsapp: "" },
        social: s.social || { facebook: "", instagram: "", twitter: "", linkedin: "", youtube: "" },
        menu_items: s.menu_items || [],
        footer_menu: s.footer_menu || [],
        page_seo: s.page_seo || {
          home: { title: "", description: "" },
          about: { title: "", description: "" },
          services: { title: "", description: "" },
          contact: { title: "", description: "" },
          book: { title: "", description: "" }
        }
      });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load settings");
    }
    setLoading(false);
  };

  const saveSiteSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/cms/site-settings`, siteSettings);
      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save settings");
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/cms/upload-logo`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      setSiteSettings(prev => ({ ...prev, logo_url: response.data.url }));
      toast.success("Logo uploaded! Click 'Save All Settings' to keep it.");
    } catch (error) {
      toast.error("Failed to upload logo");
    }
  };

  const updateContact = (field, value) => {
    setSiteSettings(prev => ({ ...prev, contact: { ...prev.contact, [field]: value } }));
  };

  const updateSocial = (field, value) => {
    setSiteSettings(prev => ({ ...prev, social: { ...prev.social, [field]: value } }));
  };

  const updatePageSeo = (page, field, value) => {
    setSiteSettings(prev => ({
      ...prev,
      page_seo: { ...prev.page_seo, [page]: { ...prev.page_seo[page], [field]: value } }
    }));
  };

  // Menu Management
  const addMenuItem = () => {
    const newItem = { id: Date.now().toString(), label: "New Link", url: "/", order: siteSettings.menu_items.length, children: [] };
    setSiteSettings(prev => ({ ...prev, menu_items: [...prev.menu_items, newItem] }));
  };

  const updateMenuItem = (id, field, value) => {
    setSiteSettings(prev => ({ ...prev, menu_items: prev.menu_items.map(item => item.id === id ? { ...item, [field]: value } : item) }));
  };

  const deleteMenuItem = (id) => {
    setSiteSettings(prev => ({ ...prev, menu_items: prev.menu_items.filter(item => item.id !== id) }));
  };

  const moveMenuItem = (id, direction) => {
    const items = [...siteSettings.menu_items].sort((a, b) => a.order - b.order);
    const index = items.findIndex(item => item.id === id);
    if ((direction === "up" && index === 0) || (direction === "down" && index === items.length - 1)) return;
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    const reordered = items.map((item, i) => ({ ...item, order: i }));
    setSiteSettings(prev => ({ ...prev, menu_items: reordered }));
  };

  const addSubMenuItem = (parentId) => {
    const newSub = { id: Date.now().toString(), label: "Sub Link", url: "/" };
    setSiteSettings(prev => ({ ...prev, menu_items: prev.menu_items.map(item => item.id === parentId ? { ...item, children: [...(item.children || []), newSub] } : item) }));
  };

  const updateSubMenuItem = (parentId, subId, field, value) => {
    setSiteSettings(prev => ({ ...prev, menu_items: prev.menu_items.map(item => item.id === parentId ? { ...item, children: (item.children || []).map(sub => sub.id === subId ? { ...sub, [field]: value } : sub) } : item) }));
  };

  const deleteSubMenuItem = (parentId, subId) => {
    setSiteSettings(prev => ({ ...prev, menu_items: prev.menu_items.map(item => item.id === parentId ? { ...item, children: (item.children || []).filter(sub => sub.id !== subId) } : item) }));
  };

  // Footer Menu
  const addFooterMenuItem = () => {
    const newItem = { id: Date.now().toString(), label: "New Link", url: "/", order: (siteSettings.footer_menu || []).length };
    setSiteSettings(prev => ({ ...prev, footer_menu: [...(prev.footer_menu || []), newItem] }));
  };

  const updateFooterMenuItem = (id, field, value) => {
    setSiteSettings(prev => ({ ...prev, footer_menu: (prev.footer_menu || []).map(item => item.id === id ? { ...item, [field]: value } : item) }));
  };

  const deleteFooterMenuItem = (id) => {
    setSiteSettings(prev => ({ ...prev, footer_menu: (prev.footer_menu || []).filter(item => item.id !== id) }));
  };

  // Page Management
  const createNewPage = () => {
    setEditingPage({ id: crypto.randomUUID(), title: "New Page", slug: "new-page-" + Date.now(), status: "draft", content_blocks: [], seo: { title: "", meta_description: "", meta_keywords: "" } });
  };

  const savePage = async () => {
    if (!editingPage) return;
    try {
      if (pages.find(p => p.id === editingPage.id)) {
        await axios.put(`${API}/cms/pages/${editingPage.id}`, editingPage);
        toast.success("Page updated!");
      } else {
        await axios.post(`${API}/cms/pages`, editingPage);
        toast.success("Page created!");
      }
      fetchData();
      setEditingPage(null);
    } catch (error) {
      toast.error("Failed to save page");
    }
  };

  const deletePage = async (pageId) => {
    if (!confirm("Delete this page?")) return;
    try {
      await axios.delete(`${API}/cms/pages/${pageId}`);
      toast.success("Page deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete page");
    }
  };

  // Content Block Management
  const addContentBlock = (type) => {
    const newBlock = {
      id: crypto.randomUUID(),
      type,
      content: type === "heading" ? { text: "New Heading", level: "h2", align: "left" }
        : type === "paragraph" ? { text: "Enter text here...", align: "left" }
        : type === "image" ? { url: "", alt: "", align: "center" }
        : type === "button" ? { text: "Click Here", url: "/book", style: "primary", align: "center" }
        : { height: 40 }
    };
    setEditingPage(prev => ({ ...prev, content_blocks: [...(prev.content_blocks || []), newBlock] }));
  };

  const updateContentBlock = (blockId, field, value) => {
    setEditingPage(prev => ({ ...prev, content_blocks: prev.content_blocks.map(b => b.id === blockId ? { ...b, content: { ...b.content, [field]: value } } : b) }));
  };

  const deleteContentBlock = (blockId) => {
    setEditingPage(prev => ({ ...prev, content_blocks: prev.content_blocks.filter(b => b.id !== blockId) }));
  };

  const moveContentBlock = (blockId, direction) => {
    const blocks = [...(editingPage.content_blocks || [])];
    const index = blocks.findIndex(b => b.id === blockId);
    if ((direction === "up" && index === 0) || (direction === "down" && index === blocks.length - 1)) return;
    const newIndex = direction === "up" ? index - 1 : index + 1;
    [blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]];
    setEditingPage(prev => ({ ...prev, content_blocks: blocks }));
  };

  if (loading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div></div>;
  }

  // Page Editor View
  if (editingPage) {
    return (
      <div className="min-h-screen bg-gray-100">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => setEditingPage(null)}><ArrowLeft size={20} className="mr-2" /> Back</Button>
              <h1 className="text-xl font-bold">{editingPage.title || "New Page"}</h1>
            </div>
            <Button onClick={savePage} className="bg-orange-500 hover:bg-orange-600"><Save size={18} className="mr-2" /> Save Page</Button>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {/* Page Settings */}
          <Card>
            <CardHeader><CardTitle>Page Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Title</Label><Input value={editingPage.title} onChange={(e) => setEditingPage({ ...editingPage, title: e.target.value })} /></div>
                <div><Label>Slug</Label><Input value={editingPage.slug} onChange={(e) => setEditingPage({ ...editingPage, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} /></div>
              </div>
              <div><Label>Status</Label>
                <Select value={editingPage.status} onValueChange={(v) => setEditingPage({ ...editingPage, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem></SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* SEO Settings */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Search size={18} /> SEO Settings</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>SEO Title (appears in browser tab & search results)</Label>
                <Input value={editingPage.seo?.title || ""} onChange={(e) => setEditingPage({ ...editingPage, seo: { ...editingPage.seo, title: e.target.value } })} placeholder="Page title for search engines" />
              </div>
              <div>
                <Label>Meta Description (appears in search results)</Label>
                <Textarea value={editingPage.seo?.meta_description || ""} onChange={(e) => setEditingPage({ ...editingPage, seo: { ...editingPage.seo, meta_description: e.target.value } })} placeholder="Brief description for search results (150-160 characters)" rows={3} />
              </div>
              <div>
                <Label>Meta Keywords</Label>
                <Input value={editingPage.seo?.meta_keywords || ""} onChange={(e) => setEditingPage({ ...editingPage, seo: { ...editingPage.seo, meta_keywords: e.target.value } })} placeholder="keyword1, keyword2, keyword3" />
              </div>
            </CardContent>
          </Card>

          {/* Content Blocks */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Content Blocks</CardTitle>
                <Select onValueChange={addContentBlock}>
                  <SelectTrigger className="w-[150px]"><SelectValue placeholder="+ Add Block" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="heading">Heading</SelectItem>
                    <SelectItem value="paragraph">Paragraph</SelectItem>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="button">Button</SelectItem>
                    <SelectItem value="spacer">Spacer</SelectItem>
                    <SelectItem value="divider">Divider</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {(!editingPage.content_blocks || editingPage.content_blocks.length === 0) ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-400">No content blocks. Add one above.</div>
              ) : (
                <div className="space-y-4">
                  {editingPage.content_blocks.map((block, index) => (
                    <div key={block.id} className="border rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col">
                            <button onClick={() => moveContentBlock(block.id, "up")} disabled={index === 0} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp size={14} /></button>
                            <button onClick={() => moveContentBlock(block.id, "down")} disabled={index === editingPage.content_blocks.length - 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown size={14} /></button>
                          </div>
                          <span className="text-sm font-medium bg-gray-100 px-2 py-1 rounded capitalize">{block.type}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteContentBlock(block.id)} className="text-red-500"><Trash2 size={16} /></Button>
                      </div>
                      {block.type === "heading" && (
                        <div className="space-y-2">
                          <Input value={block.content.text || ""} onChange={(e) => updateContentBlock(block.id, "text", e.target.value)} placeholder="Heading text" />
                          <div className="grid grid-cols-2 gap-2">
                            <Select value={block.content.level || "h2"} onValueChange={(v) => updateContentBlock(block.id, "level", v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="h1">H1</SelectItem><SelectItem value="h2">H2</SelectItem><SelectItem value="h3">H3</SelectItem><SelectItem value="h4">H4</SelectItem></SelectContent>
                            </Select>
                            <Select value={block.content.align || "left"} onValueChange={(v) => updateContentBlock(block.id, "align", v)}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                      {block.type === "paragraph" && (
                        <div className="space-y-2">
                          <Textarea value={block.content.text || ""} onChange={(e) => updateContentBlock(block.id, "text", e.target.value)} placeholder="Paragraph text" rows={4} />
                          <Select value={block.content.align || "left"} onValueChange={(v) => updateContentBlock(block.id, "align", v)}>
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                          </Select>
                        </div>
                      )}
                      {block.type === "image" && (
                        <div className="space-y-2">
                          <Input value={block.content.url || ""} onChange={(e) => updateContentBlock(block.id, "url", e.target.value)} placeholder="Image URL" />
                          <Input value={block.content.alt || ""} onChange={(e) => updateContentBlock(block.id, "alt", e.target.value)} placeholder="Alt text" />
                        </div>
                      )}
                      {block.type === "button" && (
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={block.content.text || ""} onChange={(e) => updateContentBlock(block.id, "text", e.target.value)} placeholder="Button text" />
                          <Input value={block.content.url || ""} onChange={(e) => updateContentBlock(block.id, "url", e.target.value)} placeholder="Button URL" />
                        </div>
                      )}
                      {block.type === "spacer" && (
                        <Input type="number" value={block.content.height || 40} onChange={(e) => updateContentBlock(block.id, "height", parseInt(e.target.value))} placeholder="Height in pixels" className="w-32" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/admin")}><ArrowLeft size={20} className="mr-2" /> Back to Admin</Button>
            <h1 className="text-2xl font-bold">CMS Dashboard</h1>
          </div>
          <Button onClick={saveSiteSettings} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
            {saving ? "Saving..." : <><Save size={18} className="mr-2" /> Save All Settings</>}
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="general"><Settings size={16} className="mr-2" /> General</TabsTrigger>
            <TabsTrigger value="page-seo"><Search size={16} className="mr-2" /> Page SEO</TabsTrigger>
            <TabsTrigger value="header-menu"><Menu size={16} className="mr-2" /> Header Menu</TabsTrigger>
            <TabsTrigger value="footer-menu"><Menu size={16} className="mr-2" /> Footer Menu</TabsTrigger>
            <TabsTrigger value="pages"><FileText size={16} className="mr-2" /> Route Pages</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Image size={18} /> Logo & Branding</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Logo</Label>
                    <div className="flex items-center gap-4 mt-2">
                      {siteSettings.logo_url ? (
                        <img src={siteSettings.logo_url.startsWith("http") ? siteSettings.logo_url : `${BACKEND_URL}${siteSettings.logo_url}`} alt="Logo" className="h-16 object-contain border rounded p-2" />
                      ) : (
                        <div className="h-16 w-32 border-2 border-dashed rounded flex items-center justify-center text-gray-400">No logo</div>
                      )}
                      <Input type="file" accept="image/*" onChange={handleLogoUpload} className="w-auto" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">After uploading, click "Save All Settings" to keep the logo</p>
                  </div>
                  <div><Label>Site Name</Label><Input value={siteSettings.logo_text} onChange={(e) => setSiteSettings(prev => ({ ...prev, logo_text: e.target.value }))} /></div>
                  <div><Label>Tagline</Label><Input value={siteSettings.tagline} onChange={(e) => setSiteSettings(prev => ({ ...prev, tagline: e.target.value }))} /></div>
                  <div><Label>Footer Text</Label><Input value={siteSettings.footer_text} onChange={(e) => setSiteSettings(prev => ({ ...prev, footer_text: e.target.value }))} /></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Phone size={18} /> Contact Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label><Phone size={14} className="inline mr-1" /> Phone Number</Label><Input value={siteSettings.contact?.phone || ""} onChange={(e) => updateContact("phone", e.target.value)} placeholder="+44 1234 567890" /></div>
                  <div><Label><Mail size={14} className="inline mr-1" /> Email</Label><Input value={siteSettings.contact?.email || ""} onChange={(e) => updateContact("email", e.target.value)} placeholder="info@example.com" /></div>
                  <div><Label><MapPin size={14} className="inline mr-1" /> Address</Label><Textarea value={siteSettings.contact?.address || ""} onChange={(e) => updateContact("address", e.target.value)} rows={2} /></div>
                  <div><Label><MessageCircle size={14} className="inline mr-1" /> WhatsApp</Label><Input value={siteSettings.contact?.whatsapp || ""} onChange={(e) => updateContact("whatsapp", e.target.value)} placeholder="+44 1234 567890" /></div>
                </CardContent>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader><CardTitle className="flex items-center gap-2"><Globe size={18} /> Social Media</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div><Label><Facebook size={14} className="inline mr-1" /> Facebook</Label><Input value={siteSettings.social?.facebook || ""} onChange={(e) => updateSocial("facebook", e.target.value)} /></div>
                    <div><Label><Instagram size={14} className="inline mr-1" /> Instagram</Label><Input value={siteSettings.social?.instagram || ""} onChange={(e) => updateSocial("instagram", e.target.value)} /></div>
                    <div><Label><Twitter size={14} className="inline mr-1" /> Twitter</Label><Input value={siteSettings.social?.twitter || ""} onChange={(e) => updateSocial("twitter", e.target.value)} /></div>
                    <div><Label><Linkedin size={14} className="inline mr-1" /> LinkedIn</Label><Input value={siteSettings.social?.linkedin || ""} onChange={(e) => updateSocial("linkedin", e.target.value)} /></div>
                    <div><Label><Youtube size={14} className="inline mr-1" /> YouTube</Label><Input value={siteSettings.social?.youtube || ""} onChange={(e) => updateSocial("youtube", e.target.value)} /></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Google Analytics & Search Console</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Google Analytics Code</Label>
                <textarea className="w-full h-24 p-2 border rounded-md font-mono text-sm mt-1" placeholder="Paste Google Analytics code here..." value={siteSettings?.google_analytics || ''} onChange={(e) => setSiteSettings(prev => ({...prev, google_analytics: e.target.value}))} />
              </div>
              <div>
                <Label>Google Search Console Code</Label>
                <textarea className="w-full h-24 p-2 border rounded-md font-mono text-sm mt-1" placeholder="Paste Search Console verification code here..." value={siteSettings?.google_search_console || ''} onChange={(e) => setSiteSettings(prev => ({...prev, google_search_console: e.target.value}))} />
              </div>
            </CardContent>
          </Card>
          </TabsContent>

          {/* Page SEO Tab */}
          <TabsContent value="page-seo">
            <div className="space-y-6">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Home size={18} /> Home Page SEO</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>SEO Title</Label><Input value={siteSettings.page_seo?.home?.title || ""} onChange={(e) => updatePageSeo("home", "title", e.target.value)} placeholder="Premium Taxi Service | Roman Cars" /></div>
                  <div><Label>Meta Description</Label><Textarea value={siteSettings.page_seo?.home?.description || ""} onChange={(e) => updatePageSeo("home", "description", e.target.value)} placeholder="Book reliable taxi services across UK..." rows={3} /></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Info size={18} /> About Page SEO</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>SEO Title</Label><Input value={siteSettings.page_seo?.about?.title || ""} onChange={(e) => updatePageSeo("about", "title", e.target.value)} placeholder="About Us | Roman Cars" /></div>
                  <div><Label>Meta Description</Label><Textarea value={siteSettings.page_seo?.about?.description || ""} onChange={(e) => updatePageSeo("about", "description", e.target.value)} placeholder="Learn about Roman Cars..." rows={3} /></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Briefcase size={18} /> Services Page SEO</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>SEO Title</Label><Input value={siteSettings.page_seo?.services?.title || ""} onChange={(e) => updatePageSeo("services", "title", e.target.value)} placeholder="Our Services | Roman Cars" /></div>
                  <div><Label>Meta Description</Label><Textarea value={siteSettings.page_seo?.services?.description || ""} onChange={(e) => updatePageSeo("services", "description", e.target.value)} placeholder="Explore our taxi services..." rows={3} /></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><Contact size={18} /> Contact Page SEO</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>SEO Title</Label><Input value={siteSettings.page_seo?.contact?.title || ""} onChange={(e) => updatePageSeo("contact", "title", e.target.value)} placeholder="Contact Us | Roman Cars" /></div>
                  <div><Label>Meta Description</Label><Textarea value={siteSettings.page_seo?.contact?.description || ""} onChange={(e) => updatePageSeo("contact", "description", e.target.value)} placeholder="Get in touch with Roman Cars..." rows={3} /></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2"><FileText size={18} /> Book Page SEO</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div><Label>SEO Title</Label><Input value={siteSettings.page_seo?.book?.title || ""} onChange={(e) => updatePageSeo("book", "title", e.target.value)} placeholder="Book a Taxi | Roman Cars" /></div>
                  <div><Label>Meta Description</Label><Textarea value={siteSettings.page_seo?.book?.description || ""} onChange={(e) => updatePageSeo("book", "description", e.target.value)} placeholder="Book your taxi ride now..." rows={3} /></div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Header Menu */}
          <TabsContent value="header-menu">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Header Menu (with Dropdown Support)</CardTitle>
                  <Button onClick={addMenuItem} className="bg-orange-500 hover:bg-orange-600"><Plus size={16} className="mr-1" /> Add Menu Item</Button>
                </div>
              </CardHeader>
              <CardContent>
                {siteSettings.menu_items.length === 0 ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-400">No menu items. Add one above.</div>
                ) : (
                  <div className="space-y-4">
                    {siteSettings.menu_items.sort((a, b) => a.order - b.order).map((item, index) => (
                      <div key={item.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="flex flex-col">
                            <button onClick={() => moveMenuItem(item.id, "up")} disabled={index === 0} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronUp size={16} /></button>
                            <button onClick={() => moveMenuItem(item.id, "down")} disabled={index === siteSettings.menu_items.length - 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"><ChevronDown size={16} /></button>
                          </div>
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <Input value={item.label} onChange={(e) => updateMenuItem(item.id, "label", e.target.value)} placeholder="Label" />
                            <Input value={item.url} onChange={(e) => updateMenuItem(item.id, "url", e.target.value)} placeholder="URL" />
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteMenuItem(item.id)} className="text-red-500"><Trash2 size={16} /></Button>
                        </div>
                        <div className="ml-8 border-l-2 border-orange-200 pl-4">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-500">Dropdown Items</span>
                            <Button variant="outline" size="sm" onClick={() => addSubMenuItem(item.id)}><Plus size={14} className="mr-1" /> Add Submenu</Button>
                          </div>
                          {(item.children || []).map((sub) => (
                            <div key={sub.id} className="flex items-center gap-2 mb-2">
                              <Input value={sub.label} onChange={(e) => updateSubMenuItem(item.id, sub.id, "label", e.target.value)} placeholder="Label" className="flex-1" />
                              <Input value={sub.url} onChange={(e) => updateSubMenuItem(item.id, sub.id, "url", e.target.value)} placeholder="URL" className="flex-1" />
                              <Button variant="ghost" size="sm" onClick={() => deleteSubMenuItem(item.id, sub.id)} className="text-red-500"><Trash2 size={14} /></Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Footer Menu */}
          <TabsContent value="footer-menu">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Footer Menu</CardTitle>
                  <Button onClick={addFooterMenuItem} className="bg-orange-500 hover:bg-orange-600"><Plus size={16} className="mr-1" /> Add Footer Link</Button>
                </div>
              </CardHeader>
              <CardContent>
                {(!siteSettings.footer_menu || siteSettings.footer_menu.length === 0) ? (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg text-gray-400">No footer links. Add one above.</div>
                ) : (
                  <div className="space-y-2">
                    {siteSettings.footer_menu.map((item) => (
                      <div key={item.id} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 grid grid-cols-2 gap-2">
                          <Input value={item.label} onChange={(e) => updateFooterMenuItem(item.id, "label", e.target.value)} placeholder="Label" />
                          <Input value={item.url} onChange={(e) => updateFooterMenuItem(item.id, "url", e.target.value)} placeholder="URL" />
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteFooterMenuItem(item.id)} className="text-red-500"><Trash2 size={16} /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Route Pages */}
          <TabsContent value="pages">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Route Pages ({pages.length})</h2>
              <Button onClick={createNewPage} className="bg-orange-500 hover:bg-orange-600"><Plus size={18} className="mr-2" /> Create Page</Button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pages.map((page) => (
                <Card key={page.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold">{page.title}</h3>
                        <p className="text-sm text-gray-500">/{page.slug}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${page.status === "published" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{page.status}</span>
                    </div>
                    <p className="text-sm text-gray-400 mb-3">{page.content_blocks?.length || 0} blocks</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingPage(page)}><Edit size={14} className="mr-1" /> Edit</Button>
                      <Button variant="outline" size="sm" onClick={() => deletePage(page.id)} className="text-red-500"><Trash2 size={14} /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
